import { useState, useEffect } from 'react';
import { GOLD, MUTED, BORDER, GREEN, RED, TEXT, CARD_BG } from '../constants';
import { fmt, fmtDec, today } from '../utils';
import { SectionTitle, Card, PrimaryBtn, GhostBtn, Badge } from './UI';

const POS_API   = 'https://atlantagoldandcoin.shop/api';
const LOCATION  = 4; // Johns Creek

// ── Grab the JWT from Aureus localStorage (must be on same origin, so we
//    inject a tiny helper script via a bookmarklet OR the user pastes the token)
function getStoredToken() {
  try {
    const raw = localStorage.getItem('agc_pos_token');
    if (raw) return raw;
  } catch {}
  return null;
}

function saveStoredToken(token) {
  try { localStorage.setItem('agc_pos_token', token); } catch {}
}

// Map Aureus order → AGC dashboard invoice + transactions
function mapAureusOrder(order) {
  // Payment method from paymentTransactions
  const payments = order.payment_transactions || order.paymentTransactions || [];
  let paymentMethod = '';
  if (payments.length > 0) {
    const types = [...new Set(payments.map(p => {
      const t = (p.payment?.payment_type || p.payment?.paymentType || '').toLowerCase();
      if (t.includes('cash'))   return 'Cash';
      if (t.includes('zelle'))  return 'Zelle';
      if (t.includes('venmo'))  return 'Venmo';
      if (t.includes('wire'))   return 'Wire';
      if (t.includes('check'))  return 'Check';
      if (t.includes('credit')) return 'Credit Card';
      return t || 'Other';
    }).filter(Boolean))];
    paymentMethod = types.length > 1 ? 'Multiple' : (types[0] || '');
  }

  // Staff from till
  const staff = order.till?.name || '';

  // Client name
  const user = order.user || {};
  const vendor = [user.first_name, user.last_name].filter(Boolean).join(' ') || `Client #${order.client_id}`;

  // Date
  const rawDate = order.date || order.created_at || '';
  const date = rawDate ? rawDate.split('T')[0].replace(/\//g, '-') : today();

  // Line items
  const lineItems = (order.items || []).map(item => {
    const product  = item.product || {};
    const desc     = product.name || item.description || 'Unknown Item';
    const qty      = parseFloat(item.quantity_delivered ?? item.quantity ?? 1);
    const unitPrice= parseFloat(item.unit_price ?? item.price ?? 0);
    const total    = qty * unitPrice;
    const metal    = detectMetal(desc);
    const category = detectCategory(desc, metal);
    const weight   = detectWeight(desc);
    return { description: desc, qty, unitPrice, totalAmount: total, metal, category, weight };
  });

  const total = parseFloat(order.total_amount || order.total_delivered || 0);

  return {
    invoiceNumber: `SO${order.id}`,
    vendor,
    date,
    direction:     'Sale',
    channel:       'Retail',
    paymentMethod,
    notes:         staff ? `Staff: ${staff}` : '',
    total:         total || lineItems.reduce((s, li) => s + li.totalAmount, 0),
    lineItems,
    aureusId:      order.id,
  };
}

function detectMetal(text) {
  const t = text.toUpperCase();
  if (/PALLADIUM/.test(t))                                    return 'Palladium';
  if (/PLATINUM|PLAT\b/.test(t))                             return 'Platinum';
  if (/GOLD|BUFFALO|EAGLE.*GOLD|GOLD.*EAGLE|FIRST\s*SPOUSE|LIBERTAD.*GOLD/.test(t)) return 'Gold';
  if (/SILVER|MORGAN|PEACE|WALKER|FRANKLIN|KENNEDY|BRITANNIA|MAPLE|VALIANT/.test(t)) return 'Silver';
  return 'Gold';
}
function detectCategory(desc, metal) {
  const t = desc.toUpperCase();
  if (/BAR\b/.test(t))      return `${metal} Bar`;
  if (/ROUND\b/.test(t))    return `${metal} Bar`;
  if (/JEWELRY|RING|CHAIN/.test(t)) return `${metal} Jewelry`;
  if (/SET\b|PROOF\s*SET/.test(t))  return 'Numismatic/Collectible';
  return `${metal} Coin`;
}
function detectWeight(text) {
  const t = text.toUpperCase();
  if (/10\s*OZ/.test(t)) return 10;
  if (/5\s*OZ/.test(t))  return 5;
  if (/2\s*OZ/.test(t))  return 2;
  if (/1\s*OZ/.test(t))  return 1;
  if (/1\/2\s*OZ|0\.5\s*OZ/.test(t))  return 0.5;
  if (/1\/4\s*OZ|0\.25\s*OZ/.test(t)) return 0.25;
  if (/1\/10\s*OZ|0\.1\s*OZ/.test(t)) return 0.1;
  if (/1\s*G\b|1G\b/.test(t)) return 0.0321507;
  if (/MORGAN|PEACE|SILVER\s*DOLLAR/.test(t)) return 0.7734;
  if (/KENNEDY.*HALF|HALF.*DOLLAR/.test(t))   return 0.3617;
  return 1;
}

// ─────────────────────────────────────────────────────────────────────────────

export default function POSSyncTab({ onImport, importedIds = [] }) {
  const [token,       setToken]       = useState(getStoredToken() || '');
  const [tokenInput,  setTokenInput]  = useState('');
  const [status,      setStatus]      = useState('idle'); // idle|loading|done|error
  const [message,     setMessage]     = useState('');
  const [orders,      setOrders]      = useState([]);
  const [selected,    setSelected]    = useState(new Set());
  const [dateFrom,    setDateFrom]    = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [dateTo,      setDateTo]      = useState(today());
  const [importing,   setImporting]   = useState(false);

  const hasToken = !!token;

  // ── Fetch orders from Aureus API ──────────────────────────────────────────
  async function fetchOrders() {
    if (!token) return;
    setStatus('loading');
    setMessage('Connecting to POS…');
    setOrders([]);
    setSelected(new Set());

    try {
      const startFmt = dateFrom.replace(/-/g, '/');
      const endFmt   = dateTo.replace(/-/g, '/');
      const url = `${POS_API}/orders?page=1&items_per_page=200` +
        `&filters[start_date]=${encodeURIComponent(startFmt)}` +
        `&filters[end_date]=${encodeURIComponent(endFmt)}` +
        `&filters[delivery_mode]=person` +
        `&filters[item_status]=sent` +
        `&filters[delivery_date]=true` +
        `&sort[field]=delivery_date&sort[dir]=desc` +
        `&extra=user%2Citems.product%2Cdelivery_date%2CpaymentTransactions.payment%2CpaymentTransactions.payment.paymentType`;

      const res = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      if (res.status === 401) {
        setStatus('error');
        setMessage('Token expired — paste a fresh token below.');
        return;
      }
      if (!res.ok) throw new Error(`API error ${res.status}`);

      const data = await res.json();
      const raw  = data.data || data.orders || data || [];
      const mapped = raw.map(mapAureusOrder);
      setOrders(mapped);

      // Auto-select orders not yet imported
      const newIds = new Set(
        mapped.filter(o => !importedIds.includes(o.aureusId)).map(o => o.aureusId)
      );
      setSelected(newIds);

      setStatus('done');
      setMessage(`${mapped.length} orders found · ${newIds.size} new`);
    } catch (err) {
      setStatus('error');
      setMessage(`Error: ${err.message}`);
    }
  }

  // ── Import selected orders ────────────────────────────────────────────────
  async function importSelected() {
    setImporting(true);
    const toImport = orders.filter(o => selected.has(o.aureusId));
    for (const order of toImport) {
      onImport(order);
    }
    setImporting(false);
    setMessage(`✓ Imported ${toImport.length} orders`);
    setSelected(new Set());
  }

  function toggleAll() {
    if (selected.size === orders.length) setSelected(new Set());
    else setSelected(new Set(orders.map(o => o.aureusId)));
  }

  function toggleOne(id) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  // ── Token save ────────────────────────────────────────────────────────────
  function saveToken() {
    const t = tokenInput.trim();
    if (!t) return;
    setToken(t);
    saveStoredToken(t);
    setTokenInput('');
    setMessage('Token saved — click Fetch Orders');
  }

  // ─────────────────────────────────────────────────────────────────────────

  const selectedTotal = orders
    .filter(o => selected.has(o.aureusId))
    .reduce((s, o) => s + o.total, 0);

  return (
    <div>
      <SectionTitle
        title="POS Sync"
        sub="Pull completed sales orders from Aureus into the dashboard"
      />

      {/* ── TOKEN SETUP ── */}
      {!hasToken && (
        <Card style={{ padding: 24, marginBottom: 20 }}>
          <div style={{ color: GOLD, fontSize: 12, letterSpacing: 1, marginBottom: 12 }}>CONNECT TO AUREUS POS</div>
          <div style={{ color: MUTED, fontSize: 12, marginBottom: 16, lineHeight: 1.6 }}>
            To connect, grab your session token from the POS. While logged into{' '}
            <span style={{ color: GOLD }}>atlantagoldandcoin.shop/pos</span>, open DevTools Console (F12 → Console) and run:
            <div style={{
              background: '#0A0C0E', border: `1px solid ${BORDER}`, borderRadius: 6,
              padding: '10px 14px', marginTop: 10, fontFamily: 'monospace', fontSize: 11, color: GREEN,
              userSelect: 'all',
            }}>
              {`JSON.parse(localStorage.getItem('details')).token`}
            </div>
            Copy the output and paste it below.
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <input
              value={tokenInput}
              onChange={e => setTokenInput(e.target.value)}
              placeholder="Paste Bearer token here…"
              style={{
                flex: 1, background: '#0A0C0E', border: `1px solid ${BORDER}`,
                borderRadius: 6, color: TEXT, padding: '8px 12px', fontSize: 11,
                fontFamily: 'monospace',
              }}
            />
            <PrimaryBtn onClick={saveToken}>Save Token</PrimaryBtn>
          </div>
        </Card>
      )}

      {/* ── SYNC CONTROLS ── */}
      {hasToken && (
        <Card style={{ padding: 20, marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <div style={{ color: MUTED, fontSize: 10, letterSpacing: 1, marginBottom: 4 }}>FROM</div>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                style={{ background: '#0A0C0E', border: `1px solid ${BORDER}`, borderRadius: 6, color: TEXT, padding: '7px 10px', fontSize: 12 }} />
            </div>
            <div>
              <div style={{ color: MUTED, fontSize: 10, letterSpacing: 1, marginBottom: 4 }}>TO</div>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                style={{ background: '#0A0C0E', border: `1px solid ${BORDER}`, borderRadius: 6, color: TEXT, padding: '7px 10px', fontSize: 12 }} />
            </div>
            <PrimaryBtn onClick={fetchOrders} style={{ alignSelf: 'flex-end' }}>
              {status === 'loading' ? '⟳ Fetching…' : '⟳ Fetch Orders'}
            </PrimaryBtn>
            {orders.length > 0 && selected.size > 0 && (
              <PrimaryBtn onClick={importSelected} style={{ alignSelf: 'flex-end', background: GREEN, color: '#000' }}>
                {importing ? 'Importing…' : `↓ Import ${selected.size} Orders (${fmt(selectedTotal)})`}
              </PrimaryBtn>
            )}
            <GhostBtn onClick={() => { setToken(''); saveStoredToken(''); }} style={{ alignSelf: 'flex-end', fontSize: 10 }}>
              Clear Token
            </GhostBtn>
          </div>

          {message && (
            <div style={{
              marginTop: 12, fontSize: 12, padding: '8px 12px', borderRadius: 6,
              background: status === 'error' ? '#1A0808' : status === 'done' ? '#081A0D' : '#0D1117',
              color: status === 'error' ? RED : status === 'done' ? GREEN : MUTED,
              border: `1px solid ${status === 'error' ? RED : status === 'done' ? GREEN : BORDER}30`,
            }}>
              {message}
            </div>
          )}
        </Card>
      )}

      {/* ── ORDER LIST ── */}
      {orders.length > 0 && (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '12px 18px', borderBottom: `1px solid ${BORDER}`,
            background: '#0D1117',
          }}>
            <input type="checkbox"
              checked={selected.size === orders.length}
              onChange={toggleAll}
              style={{ accentColor: GOLD, width: 15, height: 15, cursor: 'pointer' }}
            />
            <span style={{ color: MUTED, fontSize: 10, letterSpacing: 1.5 }}>
              {selected.size} OF {orders.length} SELECTED
            </span>
          </div>

          {/* Rows */}
          {orders.map(order => {
            const alreadyImported = importedIds.includes(order.aureusId);
            const isSelected      = selected.has(order.aureusId);
            return (
              <div
                key={order.aureusId}
                onClick={() => !alreadyImported && toggleOne(order.aureusId)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '12px 18px', borderBottom: `1px solid ${BORDER}`,
                  cursor: alreadyImported ? 'default' : 'pointer',
                  background: isSelected && !alreadyImported ? '#0D1A12' : 'transparent',
                  opacity: alreadyImported ? 0.45 : 1,
                  transition: 'background 0.15s',
                }}
              >
                <input type="checkbox"
                  checked={isSelected && !alreadyImported}
                  disabled={alreadyImported}
                  onChange={() => toggleOne(order.aureusId)}
                  onClick={e => e.stopPropagation()}
                  style={{ accentColor: GOLD, width: 15, height: 15, cursor: 'pointer' }}
                />
                <div style={{ minWidth: 80, color: GOLD, fontWeight: 500, fontSize: 12 }}>
                  {order.invoiceNumber}
                </div>
                <div style={{ minWidth: 90, color: MUTED, fontSize: 11 }}>{order.date}</div>
                <div style={{ flex: 1, color: TEXT, fontSize: 12 }}>{order.vendor}</div>
                <div style={{ minWidth: 60, color: MUTED, fontSize: 11 }}>
                  {order.lineItems.length} item{order.lineItems.length !== 1 ? 's' : ''}
                </div>
                {order.paymentMethod && (
                  <Badge>{order.paymentMethod}</Badge>
                )}
                {order.notes && (
                  <span style={{ color: MUTED, fontSize: 10 }}>{order.notes}</span>
                )}
                <div style={{ minWidth: 90, textAlign: 'right', color: GOLD, fontWeight: 500, fontSize: 13 }}>
                  {fmt(order.total)}
                </div>
                {alreadyImported && (
                  <span style={{ fontSize: 10, color: GREEN, minWidth: 60, textAlign: 'right' }}>✓ Imported</span>
                )}
              </div>
            );
          })}
        </Card>
      )}

      {/* ── REFRESH TOKEN PANEL (visible when token exists but expired) ── */}
      {hasToken && (
        <div style={{ marginTop: 16 }}>
          <details>
            <summary style={{ color: MUTED, fontSize: 11, cursor: 'pointer', userSelect: 'none' }}>
              Token expired or need to refresh?
            </summary>
            <Card style={{ padding: 16, marginTop: 8 }}>
              <div style={{ color: MUTED, fontSize: 11, marginBottom: 10 }}>
                Run this in the Aureus POS console (F12 → Console):
                <div style={{
                  background: '#0A0C0E', border: `1px solid ${BORDER}`, borderRadius: 6,
                  padding: '8px 12px', marginTop: 8, fontFamily: 'monospace', fontSize: 11, color: GREEN,
                  userSelect: 'all',
                }}>
                  {`JSON.parse(localStorage.getItem('details')).token`}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <input
                  value={tokenInput}
                  onChange={e => setTokenInput(e.target.value)}
                  placeholder="Paste new token…"
                  style={{
                    flex: 1, background: '#0A0C0E', border: `1px solid ${BORDER}`,
                    borderRadius: 6, color: TEXT, padding: '7px 10px', fontSize: 11,
                    fontFamily: 'monospace',
                  }}
                />
                <PrimaryBtn onClick={saveToken}>Update</PrimaryBtn>
              </div>
            </Card>
          </details>
        </div>
      )}
    </div>
  );
}
